import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { addToQuery } from 'dpl/util/queryString';
import {
  NextPageButton,
  PreviousPageButton
} from 'dpl/common/components/pagination/components/DirectionPageButton';

export function PageNumberButton({ pageNumber, isSelected, onClick }) {
  return (
    <a
      href={`?${addToQuery({ p: pageNumber }, window.location.search)}`}
      onClick={e => {
        e.preventDefault();
        onClick(pageNumber);
      }}
      className={classnames(
        'PageNumberButton bw0 mh1 flex items-center justify-center',
        { underline: isSelected }
      )}
    >
      {pageNumber}
    </a>
  );
}

PageNumberButton.propTypes = {
  pageNumber: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

export default function ExpandedPaginator({
  currentPage,
  itemsPerPage,
  totalItemCount,
  onPageClick,
  shownPageNumberPadding
}) {
  const numberOfPages = Math.ceil(totalItemCount / itemsPerPage);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === numberOfPages;
  const shouldRenderFirstEllipsis = currentPage - shownPageNumberPadding > 2;
  const shouldRenderLastEllipsis =
    currentPage + shownPageNumberPadding < numberOfPages - 1;

  return (
    <div className="ExpandedPaginator">
      <div className="inline-flex align-items-end tc">
        {!isFirstPage && (
          <PreviousPageButton
            disabled={false}
            onClick={onPageClick}
            pageNumber={currentPage - 1}
          />
        )}
        {/* first page always shown */}
        <PageNumberButton
          pageNumber={1}
          isSelected={isFirstPage}
          onClick={onPageClick}
        />
        {shouldRenderFirstEllipsis && <span className="mh1 gray pb1">...</span>}
        {Array.from(Array(shownPageNumberPadding * 2 + 1), (_, idx) => {
          const pageNumber = idx + currentPage - shownPageNumberPadding;
          return (
            pageNumber > 1 &&
            pageNumber < numberOfPages && (
              <PageNumberButton
                key={idx}
                pageNumber={pageNumber}
                isSelected={currentPage === pageNumber}
                onClick={onPageClick}
              />
            )
          );
        })}
        {shouldRenderLastEllipsis && <span className="mh1 gray pb1">...</span>}
        {/* last page always shown */}
        {numberOfPages > 1 && (
          <PageNumberButton
            pageNumber={numberOfPages}
            isSelected={isLastPage}
            onClick={onPageClick}
          />
        )}
        {!isLastPage && (
          <NextPageButton
            disabled={false}
            onClick={onPageClick}
            pageNumber={currentPage + 1}
          />
        )}
      </div>
    </div>
  );
}

ExpandedPaginator.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalItemCount: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  onPageClick: PropTypes.func.isRequired,
  /**
   * Number of page number buttons to display to the right and left of the
   * current page button.
   *
   * Example: when set to 2, and the current page is 7, two buttons (5 and
   * 6) will be rendered to the left of the 7 button; and two numbers will be
   * rendered to the right (8 and 9) 1 ... 5 6 <7> 8 9 ... 20
   */
  shownPageNumberPadding: PropTypes.number.isRequired
};
